// GraphQL client for the cafemenu checkout flow (OTP login, circle points,
// cart persistence, payment, order status). Kept separate from api.js
// (which only ever reads menu/banner data from Rista/the legacy endpoint)
// so this new, backend-writing flow is easy to audit on its own.

export const CAFEMENU_GRAPHQL_ENDPOINT = "https://api.bluetokaicoffee.com/graphql";

const AUTH_STORAGE_KEY = "btc-cafemenu-auth";
// A login lasts for one cafe visit, not forever: long enough for a few
// repeat orders, short enough that a shared/tablet device doesn't keep
// someone's Circle account open for the next person.
export const SESSION_TTL_MS = 4 * 60 * 60 * 1000;
// Spending Circle points needs a fresher proof than just browsing.
export const REWARD_REVERIFY_AFTER_MS = 60 * 60 * 1000;
const DEVICE_ID_STORAGE_KEY = "btc-cafemenu-device-id";

export class CafeMenuApiError extends Error {
  constructor(message) {
    super(message);
    this.name = "CafeMenuApiError";
  }
}

// Kept for backward compat — no longer thrown after the backend switch to
// verifyOTPForCafeMenu (which skips multi-device session enforcement).
export class CafeMenuMultiDeviceError extends CafeMenuApiError {
  constructor(message) {
    super(message);
    this.name = "CafeMenuMultiDeviceError";
  }
}

// A stable per-browser id, required by verifyOTP's device bookkeeping even
// though cafemenu isn't a native app. Generated once and persisted.
function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (!id) {
      id = "cafemenu-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return "cafemenu-" + Math.random().toString(36).slice(2);
  }
}

async function graphqlRequest(query, variables, token) {
  const headers = { "content-type": "application/json" };
  // The backend's Apollo context reads the JWT from a custom `access_token`
  // header (and an optional `device_id` for session-eviction checks) — not
  // a standard Authorization: Bearer header.
  if (token) {
    headers.access_token = token;
    headers.device_id = getOrCreateDeviceId();
  }

  let response;
  try {
    response = await fetch(CAFEMENU_GRAPHQL_ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({ query, variables }),
    });
  } catch (networkError) {
    throw new CafeMenuApiError("Unable to reach the server. Please check your connection and try again.");
  }

  let json;
  try {
    json = await response.json();
  } catch (parseError) {
    throw new CafeMenuApiError("Unexpected response from the server. Please try again.");
  }

  if (json.errors && json.errors.length > 0) {
    const message = json.errors[0]?.message || "Something went wrong. Please try again.";
    // A saved token from an earlier session can outlive the JWT's actual
    // server-side validity (it's never expiry-checked client-side before
    // use). Without this, the customer gets silently stuck on an
    // authenticated step with every call failing the same way and no path
    // back to re-verifying. Clear the stale session and let the UI bounce
    // them back to the mobile-number step instead.
    if (token && message.includes("Invalid token")) {
      clearAuthSession();
      try {
        window.dispatchEvent(new Event("cafemenu-auth-expired"));
      } catch { /* non-browser environment */ }
      throw new CafeMenuApiError("Your session has expired. Please verify your mobile number again.");
    }
    throw new CafeMenuApiError(message);
  }
  return json.data;
}

// ---- Auth (reuses the main app's OTP mutations) ----

export async function sendOtp(phoneNumber) {
  const data = await graphqlRequest(
    `mutation SendOTP($phoneNumber: String!) {
      sendOTP(phoneNumber: $phoneNumber) { status message data { isRegistered } }
    }`,
    { phoneNumber }
  );
  const result = data.sendOTP;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to send OTP");
  return result;
}

export async function verifyOtp(phoneNumber, code) {
  const data = await graphqlRequest(
    `mutation VerifyOTPForCafeMenu($phoneNumber: String!, $code: String!, $deviceId: String, $advtId: String) {
      verifyOTPForCafeMenu(phoneNumber: $phoneNumber, code: $code, deviceId: $deviceId, advtId: $advtId) {
        status
        message
        data { access_token user { id name nationalNumber } }
      }
    }`,
    {
      phoneNumber,
      code,
      deviceId: getOrCreateDeviceId(),
      advtId: getOrCreateDeviceId(),
    }
  );
  const result = data.verifyOTPForCafeMenu;
  if (String(result.status) !== "1") {
    throw new CafeMenuApiError(result.message || "Invalid or expired OTP");
  }
  return result.data;
}

export function saveAuthSession(session) {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  } catch { /* ignore quota errors */ }
}

// Returns null (and clears storage) for a missing, malformed, legacy
// (no verifiedAt) or expired session — so a stale token can never silently
// skip the OTP step.
export function loadAuthSession() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    const verifiedAt = Number(session?.verifiedAt);
    if (!session?.token || !Number.isFinite(verifiedAt) || Date.now() - verifiedAt > SESSION_TTL_MS) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function clearAuthSession() {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch { /* ignore */ }
}

// ---- Loyalty ----

export async function getLoyaltyPoints(token) {
  const data = await graphqlRequest(
    `query GetLoyaltyPoints { getLoyaltyPoints { status message data { loyaltyPoint } } }`,
    {},
    token
  );
  const result = data.getLoyaltyPoints;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to fetch circle points");
  return result.data.loyaltyPoint || 0;
}

// ---- Offers / discounts (reuses the main app's own offersList query) ----

export async function getOffers(token) {
  const data = await graphqlRequest(
    `query OffersList {
      offersList {
        status
        message
        data {
          discounts {
            id
            name
            title
            subtitle
            discountDescription
            type
            discountValue
            minRequirementAmount
            code
          }
        }
      }
    }`,
    {},
    token
  );
  const result = data.offersList;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to fetch offers");
  return result.data?.discounts || [];
}

// Client-side estimate only, for display before the server recomputes the
// authoritative amount in cafeMenuApplyDiscount (which also applies caps
// like maxAmountApplicable that aren't exposed by offersList).
export function estimateDiscountAmount(discount, itemTotal) {
  const value = Number(discount?.discountValue) || 0;
  if (String(discount?.type || "").toLowerCase() === "percentage") {
    return Math.round((itemTotal * value) / 100);
  }
  return Math.round(value);
}

// ---- Circle Rewards (POSCoupons — same catalog the app's "Circle Rewards"
// checkout tab uses), cart-aware: `active` reflects whether the cart
// actually contains an eligible item, not just point balance (unlike the
// app's own GET /pos-api/offers?screen=reward, which is balance-only). ----

export async function getCircleRewards(token, cartId) {
  const data = await graphqlRequest(
    `query ListRewards($cartId: String!) {
      cafeMenuListRewards(cartId: $cartId) {
        status
        message
        data { id name description cost active reason cardColor icon animalIcon backgroundImageUrl }
      }
    }`,
    { cartId },
    token
  );
  const result = data.cafeMenuListRewards;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to fetch rewards");
  return result.data || [];
}

export async function applyCafeMenuReward(token, cartId, posCouponId) {
  const data = await graphqlRequest(
    `mutation ApplyReward($cartId: String!, $posCouponId: Int!) {
      cafeMenuApplyReward(cartId: $cartId, posCouponId: $posCouponId) {
        status message data { ${CART_FIELDS} }
      }
    }`,
    { cartId, posCouponId },
    token
  );
  const result = data.cafeMenuApplyReward;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to apply this reward");
  return result.data;
}

export async function removeCafeMenuReward(token, cartId) {
  const data = await graphqlRequest(
    `mutation RemoveReward($cartId: String!) {
      cafeMenuRemoveReward(cartId: $cartId) {
        status message data { ${CART_FIELDS} }
      }
    }`,
    { cartId },
    token
  );
  const result = data.cafeMenuRemoveReward;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to remove the reward");
  return result.data;
}


// ---- Who is logged in (name + photo, for the menu's account chip) ----

export async function getCafeMenuProfile(token) {
  const data = await graphqlRequest(
    `query CafeMenuProfile { cafeMenuProfile { status message data { name imageUrl } } }`,
    {},
    token
  );
  const result = data.cafeMenuProfile;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to load your profile");
  return result.data || {};
}

// ---- Circle membership (rewards are members-only; everyone else sees the
// same "unlock membership" progress the app's profile page shows) ----

export async function getCircleStatus(token) {
  const data = await graphqlRequest(
    `query CircleStatus {
      cafeMenuCircleStatus {
        status
        message
        data {
          name email emailVerified
          circleStatus isMember rewardsDisabled rewardsDisabledMessage canJoin joinUrl
          progress { showMessage visitCount requiredVisitCount totalSpend requiredSpend remainingVisitCount remainingSpend eligibilityWindowEndDateText }
        }
      }
    }`,
    {},
    token
  );
  const result = data.cafeMenuCircleStatus;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to check your Circle status");
  return result.data;
}

// ---- Join Circle without leaving the menu ----
// Uses the Circle web page's own REST endpoints, driven by the encrypted user id inside the join link, so there is
// one API for the web page and the menu. The customer is already logged in (their phone is verified), so only the
// email is verified: resendEmailOtpForJoinMember emails a code, emailVerifyOtpForJoinMember checks it and joins.
//
// Call with whatever the customer has given so far; `outcome` says what comes next:
//   joined                      done
//   need_details                `missing` lists "name" / "email" to ask for
//   need_email_otp              a code was emailed to `emailMasked`; call again with `emailOtp` (or without it to resend)
//   account_conflict            more than one account matches — `message` says to contact Customer Support
//   rejected                    `message` says why
const REST_BASE_URL = CAFEMENU_GRAPHQL_ENDPOINT.replace(/\/graphql$/, "/api");

export async function joinCircle(input = {}, account = {}) {
  return joinCircleLegacy(input, account);
}

// The join link is https://circle.…/join/<encrypted user id>?membershipSource=…; the id is all the web page has too.
const parseJoinUrl = (joinUrl) => {
  try {
    const url = new URL(joinUrl);
    const match = /\/join\/([^/?#]+)/.exec(url.pathname);
    return { encryptedUserId: match ? decodeURIComponent(match[1]) : "", source: url.searchParams.get("membershipSource") || "tablet" };
  } catch {
    return { encryptedUserId: "", source: "tablet" };
  }
};

const maskEmailAddress = (email) => {
  const [local, domain] = String(email).split("@");
  return domain ? `${local.slice(0, 2)}${"*".repeat(Math.max(1, Math.min(local.length - 2, 6)))}@${domain}` : "your email";
};

async function restPost(path, body) {
  let response;
  try {
    response = await fetch(`${REST_BASE_URL}${path}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  } catch {
    throw new CafeMenuApiError("Unable to reach the server. Please check your connection and try again.");
  }
  try {
    return await response.json();
  } catch {
    throw new CafeMenuApiError("Unexpected response from the server. Please try again.");
  }
}

const isRealName = (name) => name.length >= 2 && /\p{L}/u.test(name) && !/^guest$/i.test(name);

async function joinCircleLegacy(input, account) {
  const { encryptedUserId, source } = parseJoinUrl(account.joinUrl);
  const conflict = { outcome: "account_conflict", message: "We found multiple accounts linked to your details. Please contact Customer Support to complete your enrollment." };
  if (!encryptedUserId) return { outcome: "rejected", message: "We couldn't start your membership. Please try again." };

  const typedName = String(input.name || "").trim().replace(/\s+/g, " ");
  const name = typedName || String(account.name || "").trim();
  const email = String(input.email || account.email || "").trim().toLowerCase();
  const missing = [];
  if (!isRealName(name)) missing.push("name");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) missing.push("email");
  if (missing.length) {
    return { outcome: "need_details", message: missing.length === 2 ? "Add your name and email to join Circle." : missing[0] === "name" ? "Add your name to join Circle." : "Add your email to join Circle.", missing, name: isRealName(name) ? name : null, email: email || null };
  }

  // the number is the one they logged in with (the endpoint wants it; it is not asked for again)
  const phoneNumber = String(account.phone || "").replace(/\D/g, "").slice(-10);
  if (phoneNumber.length !== 10) return { outcome: "rejected", message: "Please verify your mobile number again and retry." };

  const body = { encrptedUserId: encryptedUserId, encryptedUserId, phoneNumber, email, name, membershipSource: source };
  const ok = (r) => String(r?.status) === "1";
  const masked = maskEmailAddress(email);

  if (!input.emailOtp) {
    const sent = await restPost("/resendEmailOtpForJoinMember", body);
    if (!ok(sent)) return { outcome: "rejected", message: "We couldn't send the code. Please try again." };
    return { outcome: "need_email_otp", message: "", emailMasked: masked, resendAfterSeconds: 30 };
  }

  const done = await restPost("/emailVerifyOtpForJoinMember", { ...body, emailOtp: String(input.emailOtp) });
  const d = done?.data || {};
  if (d.accountConflict === true) return conflict;
  if (ok(done) && (d.circleStatus === "Member" || Number(d.lpEligible) === 1)) return { outcome: "joined", message: "Welcome to Circle!" };
  const message = String(done?.message || "");
  if (/otp/i.test(message)) return { outcome: "need_email_otp", message: "That code isn't right or has expired. Check it and try again.", emailMasked: masked, resendAfterSeconds: 0 };
  if (/not found on shopify/i.test(message)) return { outcome: "rejected", message: "We couldn't find your customer profile. Please contact Customer Support to complete your enrollment." };
  return { outcome: "rejected", message: "We couldn't complete your membership. Please try again." };
}

// ---- Cart / payment ----

const CART_FIELDS = `
  cartId branchCode tableNumber
  items { skuCode ristaProductId name quantity unitPrice taxRatePercent addons { name unitPrice } }
  itemTotal appliedLoyaltyPoints
  appliedDiscountId appliedDiscountName appliedDiscountAmount
  appliedRewardId appliedRewardName appliedRewardCost appliedRewardDiscountAmount appliedRewardLockReferenceId
  taxAmount totalPrice status
`;

// What the cart costs, as the server computes it (the amount that will be
// charged): items − reward/offer, then GST on what is left.
export const totalsOf = (cart) => ({
  cartId: cart.cartId,
  itemTotal: Number(cart.itemTotal) || 0,
  taxAmount: Number(cart.taxAmount) || 0,
  totalPrice: Number(cart.totalPrice) || 0,
});

export async function getCafeMenuCart(token, cartId) {
  const data = await graphqlRequest(
    `query GetCart($cartId: String!) {
      cafeMenuGetCart(cartId: $cartId) {
        status message data { ${CART_FIELDS} }
      }
    }`,
    { cartId },
    token
  );
  const result = data.cafeMenuGetCart;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to load your cart");
  return result.data;
}

// Cart items as the backend wants them: the item's own price, with each
// add-on listed separately. A reward covers the item only — the add-ons stay
// payable — so the add-on price must NOT be folded into unitPrice here.
// Free (₹0) add-ons go too, so the POS bill shows them with their SKU — but only
// real Rista options (linked to a menu item); the adapter's placeholder option
// for a missing option set has no item and the server would reject it.
const isSentAddon = (o) => Number(o?.unitPrice ?? o?.price ?? 0) > 0 || Boolean(o?.itemId || o?.skuCode);

export function toCafeMenuApiItems(items, taxRateFor) {
  return (items || []).map((item) => {
    const options = (item.selectedOptions || []).filter(isSentAddon);
    const optionsExtra = options.reduce((sum, o) => sum + Number(o?.unitPrice ?? o?.price ?? 0), 0);
    const base = Number.isFinite(Number(item.baseUnitPrice))
      ? Number(item.baseUnitPrice)
      : Math.max(0, Number(item.unitPrice || 0) - optionsExtra);
    return {
      skuCode: item.skuCode || item.itemId || item.productId,
      ristaProductId: String(item.itemId || item.productId || ""),
      name: item.name,
      quantity: item.quantity,
      unitPrice: base,
      // GST % for this item (0 when its price already includes tax); the server
      // adds the tax on top of whatever is left after any reward/offer.
      taxRatePercent: taxRateFor ? Number(taxRateFor(item)) || 0 : 0,
      // optionId lets the server pick the exact Rista option (and so its SKU) instead of matching by name.
      addons: options.map((o) => ({
        name: o.name || o.title || "",
        unitPrice: Number(o.unitPrice ?? o.price ?? 0),
        optionId: String(o.optionId || ""),
      })),
    };
  });
}

// The backend takes `tableName` (the table exactly as the POS names it). If it is an older backend that doesn't
// know the argument yet, the save is retried without it (and that is remembered) so ordering never breaks.
let backendKnowsTableName = true;
// Same for the add-ons' optionId: an older backend rejects the field, so it is dropped and the save retried.
let backendKnowsAddonOptionId = true;

const withoutAddonOptionIds = (items) => (items || []).map((item) => ({
  ...item,
  addons: (item.addons || []).map(({ optionId, ...addon }) => addon),
}));

export async function saveCafeMenuCart(token, input) {
  const sendName = backendKnowsTableName && Boolean(input.tableName);
  const { tableName, ...fields } = input;
  const rest = backendKnowsAddonOptionId ? fields : { ...fields, items: withoutAddonOptionIds(fields.items) };
  try {
    const data = await graphqlRequest(
      `mutation SaveCart($cartId: String, $branchCode: String!, $items: [CafeMenuItemInput!]!, $tableNumber: String, ${sendName ? "$tableName: String, " : ""}$specialInstructions: String) {
      cafeMenuSaveCart(cartId: $cartId, branchCode: $branchCode, items: $items, tableNumber: $tableNumber, ${sendName ? "tableName: $tableName, " : ""}specialInstructions: $specialInstructions) {
        status message data { ${CART_FIELDS} }
      }
    }`,
      sendName ? { ...rest, tableName } : rest,
      token
    );
    const result = data.cafeMenuSaveCart;
    if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to save your cart");
    return result.data;
  } catch (e) {
    if (sendName && /tableName/.test(String(e?.message))) {
      backendKnowsTableName = false;
      return saveCafeMenuCart(token, input);
    }
    if (backendKnowsAddonOptionId && /optionId/.test(String(e?.message))) {
      backendKnowsAddonOptionId = false;
      return saveCafeMenuCart(token, input);
    }
    throw e;
  }
}

export async function applyCafeMenuLoyaltyPoints(token, cartId, points) {
  const data = await graphqlRequest(
    `mutation ApplyPoints($cartId: String!, $points: Int!) {
      cafeMenuApplyLoyaltyPoints(cartId: $cartId, points: $points) {
        status message data { ${CART_FIELDS} }
      }
    }`,
    { cartId, points },
    token
  );
  const result = data.cafeMenuApplyLoyaltyPoints;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to apply circle points");
  return result.data;
}

export async function applyCafeMenuDiscount(token, cartId, discountId) {
  const data = await graphqlRequest(
    `mutation ApplyDiscount($cartId: String!, $discountId: Int!) {
      cafeMenuApplyDiscount(cartId: $cartId, discountId: $discountId) {
        status message data { ${CART_FIELDS} }
      }
    }`,
    { cartId, discountId },
    token
  );
  const result = data.cafeMenuApplyDiscount;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to apply this offer");
  return result.data;
}

export async function removeCafeMenuDiscount(token, cartId) {
  const data = await graphqlRequest(
    `mutation RemoveDiscount($cartId: String!) {
      cafeMenuRemoveDiscount(cartId: $cartId) {
        status message data { ${CART_FIELDS} }
      }
    }`,
    { cartId },
    token
  );
  const result = data.cafeMenuRemoveDiscount;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to remove the offer");
  return result.data;
}

export async function initiateCafeMenuPayment(token, cartId) {
  const data = await graphqlRequest(
    `mutation InitiatePayment($cartId: String!) {
      cafeMenuInitiatePayment(cartId: $cartId) {
        status message data { razorpayOrderId amount currency keyId isFree }
      }
    }`,
    { cartId },
    token
  );
  const result = data.cafeMenuInitiatePayment;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to start payment");
  return result.data;
}

export async function getCafeMenuOrderStatus(token, cartId) {
  const data = await graphqlRequest(
    `query OrderStatus($cartId: String!) {
      cafeMenuGetOrderStatus(cartId: $cartId) {
        status message data { cartStatus ristaPushStatus ristaInvoiceNumber earnedLoyaltyPoints }
      }
    }`,
    { cartId },
    token
  );
  const result = data.cafeMenuGetOrderStatus;
  if (String(result.status) !== "1") throw new CafeMenuApiError(result.message || "Unable to fetch order status");
  return result.data;
}
